<template>
  <div>
    <v-btn
      @click="toggleAddDialog()"
      class="info text--white"
      slot="activator"
      >Add Availability
    </v-btn>
    <v-dialog :value="addPersonAvailabilityDialog" max-width="600px">
      <v-card>
        <v-card-title
          class="pt-1 pb-1 headline grey lighten-2"
          color="primary"
          primary-title
        >
          <span class="headline">Add Availability</span>
          <v-spacer />
          <v-btn @click="closeNewItem()" fab small text>
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex xs12>
                <v-select
                  :error-messages="addFiscalYearErrors"
                  :items="fiscalYearsRemainingForPersonExceptEditYearList"
                  @blur="$v.addFiscalYear.$touch()"
                  @input="$v.addFiscalYear.$touch()"
                  label="Year (FY)"
                  v-model="addFiscalYear"
                />
              </v-flex>
              <v-flex md4 sm6 xs12>
                <v-text-field
                  :error-messages="addWorkingDaysErrors"
                  @blur="$v.addWorkingDays.$touch()"
                  @input="$v.addWorkingDays.$touch()"
                  label="Working Days"
                  max="365"
                  min="0"
                  step="0.5"
                  type="number"
                  v-model="addWorkingDays"
                />
              </v-flex>
              <v-flex md4 sm6 xs12>
                <v-text-field
                  :error-messages="addHolidayEntitlementErrors"
                  @blur="$v.addHolidayEntitlement.$touch()"
                  @input="$v.addHolidayEntitlement.$touch()"
                  label="Holiday"
                  max="100"
                  min="0"
                  step="0.5"
                  type="number"
                  v-model="addHolidayEntitlement"
                />
              </v-flex>
              <v-flex md4 sm6 xs12>
                <v-text-field
                  :error-messages="addCarriedOverErrors"
                  @blur="$v.addCarriedOver.$touch()"
                  @input="$v.addCarriedOver.$touch()"
                  label="Carried Over"
                  max="100"
                  min="0"
                  step="0.5"
                  type="number"
                  v-model="addCarriedOver"
                />
              </v-flex>
              <v-flex md4 sm6 xs12>
                <v-text-field
                  :error-messages="addSpecialAllowanceErrors"
                  @blur="$v.addSpecialAllowance.$touch()"
                  @input="$v.addSpecialAllowance.$touch()"
                  label="Special Allowance"
                  max="100"
                  min="0"
                  step="0.5"
                  type="number"
                  v-model="addSpecialAllowance"
                />
              </v-flex>
              <v-flex md4 sm6 xs12>
                <v-text-field
                  :error-messages="addBankHolidayErrors"
                  @blur="$v.addBankHoliday.$touch()"
                  @input="$v.addBankHoliday.$touch()"
                  label="Bank Holiday"
                  max="100"
                  min="0"
                  step="0.5"
                  type="number"
                  v-model="addBankHoliday"
                />
              </v-flex>
              <v-flex md12 sm12 xs12>
                <v-text-field label="Comments" v-model="addComments" />
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="saveNewItemAndCloseDialog" color="green" text>
            Save
          </v-btn>
          <v-btn @click="closeNewItem" color="red darken-1" text>
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
FETCH_PERSON_AVAILABILITY_LIST,
SAVE_NEW_PERSON_AVAILABILITY,
} from "@/store/person_availability/actions.js";
import {
RESET_DEFAULT_NEW_PERSON_AVAILABILITY,
TOGGLE_NEW_PERSON_AVAILABILITY_DIALOG,
UPDATE_ADD_PERSON_AVAILABILITY_FIELD,
} from "@/store/person_availability/mutations.js";
import { required } from "vuelidate/lib/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";
import EditTableFunctions from "../editTableFunctions";

export default {
  name: "AddPersonAvailabilityDialog",
  validations: {
    addFiscalYear: { required },
    addWorkingDays: { required },
    addCarriedOver: { required },
    addHolidayEntitlement: { required },
    addSpecialAllowance: { required },
    addBankHoliday: { required }
  },
  mixins: [EditTableFunctions],
  props: {
    personId: {
      type: Number,
      required: true,
    },
  },

  computed: {
    ...mapGetters(["employmentTypeList", "jobList"]),
    ...mapGetters([
      "addPersonAvailabilityDialog",
      "fiscalYearList",
      "fiscalYearsRemainingForPersonExceptEditYearList",
      "person",
    ]),
    addFiscalYear: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability.year;
      },
      set(value) {
        this.updateNewValue("year", value);
      },
    },
    addFiscalYearErrors() {
      const errors = [];
      if (!this.$v.addFiscalYear.$dirty) return errors;
      !this.$v.addFiscalYear.required && errors.push("required.");
      return errors;
    },
    addCarriedOver: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability
          .carriedOver;
      },
      set(value) {
        value = Math.abs(value);
        this.updateNewValue("carriedOver", value);
      },
    },
    addCarriedOverErrors() {
      const errors = [];
      if (!this.$v.addCarriedOver.$dirty) return errors;
      !this.$v.addCarriedOver.required && errors.push("required.");
      return errors;
    },
    addWorkingDays: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability
          .workingDays;
      },
      set(value) {
        value = Math.abs(value);
        this.updateNewValue("workingDays", value);
      },
    },
    addWorkingDaysErrors() {
      const errors = [];
      if (!this.$v.addWorkingDays.$dirty) return errors;
      !this.$v.addWorkingDays.required && errors.push("required.");
      return errors;
    },
    addBankHoliday: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability
          .bankHolidayDays;
      },
      set(value) {
        value = Math.abs(value);
        this.updateNewValue("bankHolidayDays", value);
      },
    },
    addBankHolidayErrors() {
      const errors = [];
      if (!this.$v.addBankHoliday.$dirty) return errors;
      !this.$v.addBankHoliday.required && errors.push("required.");
      return errors;
    },
    addHolidayEntitlement: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability
          .holidayEntitlement;
      },
      set(value) {
        value = Math.abs(value);
        this.updateNewValue("holidayEntitlement", value);
      },
    },
    addHolidayEntitlementErrors() {
      const errors = [];
      if (!this.$v.addHolidayEntitlement.$dirty) return errors;
      !this.$v.addHolidayEntitlement.required && errors.push("required.");
      return errors;
    },
    addSpecialAllowance: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability
          .specialAllowance;
      },
      set(value) {
        value = Math.abs(value);
        this.updateNewValue("specialAllowance", value);
      },
    },
    addSpecialAllowanceErrors() {
      const errors = [];
      if (!this.$v.addSpecialAllowance.$dirty) return errors;
      !this.$v.addSpecialAllowance.required && errors.push("required.");
      return errors;
    },
    addComments: {
      get() {
        return this.$store.state.person_availability.addPersonAvailability
          .comments;
      },
      set(value) {
        this.updateNewValue("comments", value);
      },
    },
  },

  mounted() {
    if (this.personId !== undefined) {
      this.resetNewItemToDefault(this.personId);
      this.updateNewValue("personId", this.personId);
    }
  },

  methods: {
    ...mapActions({
      fetchTableList: FETCH_PERSON_AVAILABILITY_LIST,
      saveNewTableItemRecord: SAVE_NEW_PERSON_AVAILABILITY,
    }),
    //
    ...mapMutations({
      toggleAddDialog: TOGGLE_NEW_PERSON_AVAILABILITY_DIALOG,
      // loadTableItemToEdit: LOAD_PERSON_SUMMARY_TO_EDIT,
      updateTableItemToAdd: UPDATE_ADD_PERSON_AVAILABILITY_FIELD,
      resetNewItemToDefault: RESET_DEFAULT_NEW_PERSON_AVAILABILITY,
    }),
  },
};
</script>

<style scoped>
</style>
